import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { ImpersonationProvider, useImpersonation } from '@/contexts/ImpersonationContext'
import { useAuth } from '@/contexts/AuthContext'

// Layouts
import { AuthLayout } from '@/layouts/AuthLayout'
import { AdminLayout } from '@/layouts/AdminLayout'
import { UserLayout } from '@/layouts/UserLayout'
import { OttoLayout } from '@/layouts/OttoLayout'
import { RequireFullAccount, RequireOttoOnly } from '@/components/OttoGates'

// Auth pages
import { LoginPage } from '@/pages/auth/LoginPage'
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage'
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage'
import { AcceptInvitePage } from '@/pages/auth/AcceptInvitePage'

// Admin pages
import { AdminDashboardPage } from '@/pages/admin/AdminDashboardPage'
import { AccountsListPage } from '@/pages/admin/AccountsListPage'
import { AccountFormPage } from '@/pages/admin/AccountFormPage'
import { UsersListPage } from '@/pages/admin/UsersListPage'
import { UserFormPage } from '@/pages/admin/UserFormPage'
import { AdminProfilePage } from '@/pages/admin/AdminProfilePage'
import { OttoAdminsPage } from '@/pages/admin/OttoAdminsPage'
import { OttoOperatorInvitePage } from '@/pages/admin/OttoOperatorInvitePage'
import { OttoStatusPage } from '@/pages/user/OttoStatusPage'
import { AdminSettingsPage } from '@/pages/admin/AdminSettingsPage'
import { AdminLeadsPage } from '@/pages/admin/AdminLeadsPage'
import { AdminLeadDetailPage } from '@/pages/admin/AdminLeadDetailPage'
import GHLLocationListPage from '@/pages/admin/GHLLocationListPage'
import GHLDashboardPage from '@/pages/admin/GHLDashboardPage'
import GHLAnalysisDetailPage from '@/pages/admin/GHLAnalysisDetailPage'
import GHLQualifierLocationListPage from '@/pages/admin/GHLQualifierLocationListPage'
import GHLQualifierDashboardPage from '@/pages/admin/GHLQualifierDashboardPage'
import QualifiedLeadDetailPage from '@/pages/admin/QualifiedLeadDetailPage'
import ConvertedLeadDetailPage from '@/pages/admin/ConvertedLeadDetailPage'
import PhoneCallLeadDetailPage from '@/pages/admin/PhoneCallLeadDetailPage'
import { OnboardingListPage } from '@/pages/admin/onboarding/OnboardingListPage'
import { OnboardingRunPage } from '@/pages/admin/onboarding/OnboardingRunPage'

/**
 * Bare-location URL → default tab redirect that PRESERVES the search string.
 * `<Navigate to="feed">` (string form) drops `?from&until&range_label` etc.,
 * which breaks the URL-backed time-range filter when a user clicks through
 * from the location list page. Reading `location.search` and passing it via
 * the `To` object keeps the filter intact across the redirect.
 */
function GHLLocationFeedRedirect() {
  const location = useLocation()
  return <Navigate to={{ pathname: 'feed', search: location.search }} replace />
}

/**
 * Same redirect pattern as `GHLLocationFeedRedirect`, but for the qualifier
 * detail page. Bare `/location/:locationId` URLs land on the default tab
 * while preserving the URL-backed time range filter.
 */
function GHLQualifierLocationDefaultRedirect() {
  const location = useLocation()
  return <Navigate to={{ pathname: 'qualified-leads', search: location.search }} replace />
}

// User pages
import { UserDashboardPage } from '@/pages/user/UserDashboardPage'
import { ProfilePage } from '@/pages/user/ProfilePage'
import { InviteUserPage } from '@/pages/user/InviteUserPage'
import { SettingsLayout, IntegrationsPage, AccountSettingsPage, BusinessProfilePage, BillingPage, AIAssistantPage, AirportSettingsPage, LeadScoringPage, SEOAttributionPage, BookingSettingsPage, PricingSettingsPage } from '@/pages/user/settings'
import { ReviewsPage } from '@/pages/user/ReviewsPage'
import { FAQsPage } from '@/pages/user/FAQsPage'
import { VehiclesPage } from '@/pages/user/VehiclesPage'
import { VehicleFormPage } from '@/pages/user/VehicleFormPage'
import { VehicleDetailPage } from '@/pages/user/VehicleDetailPage'
import { CustomPagesPage } from '@/pages/user/CustomPagesPage'
import { EditCustomPagePage } from '@/pages/user/EditCustomPagePage'
import { BookingsPage } from '@/pages/user/BookingsPage'
import { BookingDetailPage } from '@/pages/user/BookingDetailPage'
import { QuotesPage } from '@/pages/user/QuotesPage'
import { QuoteDetailPage } from '@/pages/user/QuoteDetailPage'
import { LeadsPage } from '@/pages/user/LeadsPage'
import { LeadDetailPage } from '@/pages/user/LeadDetailPage'
import { PagesPage } from '@/pages/user/PagesPage'
import { PageEditorPage } from '@/pages/user/PageEditorPage'
import { DocsPage } from '@/pages/docs/DocsPage'
import { KnowledgeBasePage } from '@/pages/user/KnowledgeBasePage'
import { CallbacksPage } from '@/pages/user/CallbacksPage'
import { ChatHistoryPage } from '@/pages/user/ChatHistoryPage'

// Components
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { LoadingSpinner } from '@/components/ui/loading-spinner'

/**
 * BuilderApp - The admin/management application
 * This is the main app for authenticated users managing their accounts
 */
export function BuilderApp() {
  const { isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <ImpersonationProvider>
      <Routes>
        {/* Public routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
        </Route>

        {/* Admin routes */}
        <Route
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/admin/accounts" element={<AccountsListPage />} />
          <Route path="/admin/accounts/new" element={<AccountFormPage />} />
          <Route path="/admin/accounts/:id" element={<AccountFormPage />} />
          <Route path="/admin/otto-operators/new" element={<OttoOperatorInvitePage />} />
          <Route path="/admin/otto-admins" element={<OttoAdminsPage />} />
          <Route path="/admin/miles-operators/new" element={<Navigate to="/admin/otto-operators/new" replace />} />
          <Route path="/admin/users" element={<UsersListPage />} />
          <Route path="/admin/users/new" element={<UserFormPage />} />
          <Route path="/admin/users/:id" element={<UserFormPage />} />
          <Route path="/admin/leads" element={<AdminLeadsPage />} />
          <Route path="/admin/leads/:id" element={<AdminLeadDetailPage />} />
          <Route path="/admin/ghl-middleware" element={<GHLLocationListPage />} />
          {/* Bare location URL → redirect to the default tab (feed). Wrapper
              component preserves the search string (?from&until&range_label
              from the URL-backed time filter); a plain <Navigate to="feed">
              would drop them. Keeps older bookmarks + the list page's
              navigate() call working unchanged. */}
          <Route
            path="/admin/ghl-middleware/location/:locationId"
            element={<GHLLocationFeedRedirect />}
          />
          <Route path="/admin/ghl-middleware/location/:locationId/:tab" element={<GHLDashboardPage />} />
          <Route path="/admin/ghl-middleware/analysis/:id" element={<GHLAnalysisDetailPage />} />
          {/* GHL Qualifier (new app) — admin dashboard for the lead-qualification
              + converted-lead lifecycle pipeline. Mirrors the middleware's
              shape (list → detail with tabs → row detail) so operators
              recognize the navigation. */}
          <Route path="/admin/ghl-qualifier" element={<GHLQualifierLocationListPage />} />
          <Route
            path="/admin/ghl-qualifier/location/:locationId"
            element={<GHLQualifierLocationDefaultRedirect />}
          />
          <Route
            path="/admin/ghl-qualifier/location/:locationId/:tab"
            element={<GHLQualifierDashboardPage />}
          />
          <Route
            path="/admin/ghl-qualifier/qualified/:id"
            element={<QualifiedLeadDetailPage />}
          />
          <Route
            path="/admin/ghl-qualifier/converted/:id"
            element={<ConvertedLeadDetailPage />}
          />
          <Route
            path="/admin/ghl-qualifier/phone-call/:id"
            element={<PhoneCallLeadDetailPage />}
          />
          <Route path="/admin/onboarding" element={<OnboardingListPage />} />
          <Route path="/admin/onboarding/:runId" element={<OnboardingRunPage />} />
          <Route path="/admin/profile" element={<AdminProfilePage />} />
          <Route path="/admin/settings" element={<AdminSettingsPage />} />
        </Route>

        {/* Impersonation routes - uses UserLayout for same experience as user */}
        <Route
          element={
            <ProtectedRoute requiredRole="admin">
              <UserLayout />
            </ProtectedRoute>
          }
        >
          <Route
            path="/admin/accounts/:accountSlug/view/*"
            element={<ImpersonatedUserRoutes />}
          />
        </Route>

        {/* Otto-only operator portal */}
        <Route
          element={
            <ProtectedRoute requiredRole="user">
              <RequireOttoOnly>
                <OttoLayout />
              </RequireOttoOnly>
            </ProtectedRoute>
          }
        >
          <Route path="/otto" element={<OttoStatusPage />} />
          {/* Pre-rebrand path, still in operators' bookmarks. */}
          <Route path="/miles" element={<Navigate to="/otto" replace />} />
        </Route>

        {/* User routes */}
        <Route
          element={
            <ProtectedRoute requiredRole="user">
              <RequireFullAccount>
                <UserLayout />
              </RequireFullAccount>
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<UserDashboardPage />} />
          <Route path="/dashboard/reviews" element={<ReviewsPage />} />
          <Route path="/dashboard/otto-ai" element={<OttoStatusPage />} />
          <Route path="/dashboard/miles-ai" element={<Navigate to="/dashboard/otto-ai" replace />} />
          <Route path="/dashboard/faqs" element={<FAQsPage />} />
          <Route path="/dashboard/vehicles" element={<VehiclesPage />} />
          {/* Static segments outrank /vehicles/:id in React Router's ranking, so
              these resolve to the filtered list rather than a vehicle detail. */}
          <Route path="/dashboard/vehicles/active" element={<VehiclesPage filter="active" />} />
          <Route path="/dashboard/vehicles/inactive" element={<VehiclesPage filter="inactive" />} />
          <Route path="/dashboard/vehicles/new" element={<VehicleFormPage />} />
          <Route path="/dashboard/vehicles/:id" element={<VehicleDetailPage />} />
          <Route path="/dashboard/vehicles/:id/edit" element={<VehicleFormPage />} />
          <Route path="/dashboard/pages" element={<PagesPage />} />
          <Route path="/dashboard/pages/:pageId" element={<PageEditorPage />} />
          <Route path="/dashboard/custom-pages" element={<CustomPagesPage />} />
          <Route path="/dashboard/custom-pages/:id/edit" element={<EditCustomPagePage />} />
          <Route path="/dashboard/bookings" element={<BookingsPage />} />
          <Route path="/dashboard/bookings/:id" element={<BookingDetailPage />} />
          <Route path="/dashboard/quotes" element={<QuotesPage />} />
          <Route path="/dashboard/quotes/:id" element={<QuoteDetailPage />} />
          <Route path="/dashboard/leads" element={<LeadsPage />} />
          <Route path="/dashboard/leads/:id" element={<LeadDetailPage />} />
          <Route path="/dashboard/chat-history" element={<ChatHistoryPage />} />
          <Route path="/dashboard/knowledge-base" element={<KnowledgeBasePage />} />
          <Route path="/dashboard/callbacks" element={<CallbacksPage />} />
          <Route path="/dashboard/docs/*" element={<DocsPage />} />
          <Route path="/dashboard/profile" element={<ProfilePage />} />
          <Route path="/dashboard/invite" element={<InviteUserPage />} />
          <Route path="/dashboard/settings" element={<SettingsLayout />}>
            <Route index element={<Navigate to="business" replace />} />
            <Route path="business" element={<BusinessProfilePage />} />
            <Route path="website" element={<Navigate to="../business?tab=website" replace />} />
            <Route path="branding" element={<Navigate to="../business?tab=branding" replace />} />
            <Route path="seo" element={<SEOAttributionPage />} />
            <Route path="conversion-labels" element={<Navigate to="../seo?tab=conversion-labels" replace />} />
            <Route path="booking" element={<BookingSettingsPage />} />
            <Route path="balance-reminders" element={<Navigate to="../booking?tab=deposit-settings" replace />} />
            <Route path="pricing" element={<PricingSettingsPage />} />
            <Route path="flat-rates" element={<Navigate to="../pricing?tab=flat-rates" replace />} />
            <Route path="zip-flat-rates" element={<Navigate to="../pricing?tab=zip-flat-rates" replace />} />
            <Route path="deadhead-fees" element={<Navigate to="../pricing?tab=deadhead-fees" replace />} />
            <Route path="airports" element={<AirportSettingsPage />} />
            <Route path="ai-assistant" element={<AIAssistantPage />} />
            <Route path="lead-scoring" element={<LeadScoringPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="account" element={<AccountSettingsPage />} />
          </Route>
        </Route>

        {/* Default redirect */}
        <Route path="/" element={<RootRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ImpersonationProvider>
  )
}

// Component to handle root redirect based on role
function RootRedirect() {
  const { isAuthenticated, isAdmin, profile, account } = useAuth()

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  if (isAdmin) {
    return <Navigate to="/admin" replace />
  }
  // The account row loads a beat after the profile — wait, don't guess.
  if (profile?.account_id && !account) {
    return null
  }
  if (account?.account_type === 'miles_only') {
    return <Navigate to="/otto" replace />
  }
  return <Navigate to="/dashboard" replace />
}

// Component to render user routes in impersonation mode
function ImpersonatedUserRoutes() {
  const { impersonatedAccount } = useImpersonation()
  if (impersonatedAccount?.account_type === 'miles_only') {
    return (
      <Routes>
        <Route path="*" element={<OttoStatusPage adminAccount={impersonatedAccount} />} />
      </Routes>
    )
  }
  return (
    <Routes>
      <Route path="dashboard" element={<UserDashboardPage />} />
      <Route path="reviews" element={<ReviewsPage />} />
      <Route path="otto-ai" element={<OttoStatusPage adminAccount={impersonatedAccount ?? undefined} />} />
      <Route path="miles-ai" element={<Navigate to="../otto-ai" replace />} />
      <Route path="faqs" element={<FAQsPage />} />
      <Route path="vehicles" element={<VehiclesPage />} />
      <Route path="vehicles/active" element={<VehiclesPage filter="active" />} />
      <Route path="vehicles/inactive" element={<VehiclesPage filter="inactive" />} />
      <Route path="vehicles/new" element={<VehicleFormPage />} />
      <Route path="vehicles/:id" element={<VehicleDetailPage />} />
      <Route path="vehicles/:id/edit" element={<VehicleFormPage />} />
      <Route path="pages" element={<PagesPage />} />
      <Route path="pages/:pageId" element={<PageEditorPage />} />
      <Route path="custom-pages" element={<CustomPagesPage />} />
      <Route path="custom-pages/:id/edit" element={<EditCustomPagePage />} />
      <Route path="bookings" element={<BookingsPage />} />
      <Route path="bookings/:id" element={<BookingDetailPage />} />
      <Route path="quotes" element={<QuotesPage />} />
      <Route path="quotes/:id" element={<QuoteDetailPage />} />
      <Route path="leads" element={<LeadsPage />} />
      <Route path="leads/:id" element={<LeadDetailPage />} />
      <Route path="chat-history" element={<ChatHistoryPage />} />
      <Route path="knowledge-base" element={<KnowledgeBasePage />} />
      <Route path="callbacks" element={<CallbacksPage />} />
      <Route path="docs/*" element={<DocsPage />} />
      <Route path="profile" element={<ProfilePage />} />
      <Route path="invite" element={<InviteUserPage />} />
      <Route path="settings" element={<SettingsLayout />}>
        <Route index element={<Navigate to="business" replace />} />
        <Route path="business" element={<BusinessProfilePage />} />
        <Route path="website" element={<Navigate to="../business?tab=website" replace />} />
        <Route path="branding" element={<Navigate to="../business?tab=branding" replace />} />
        <Route path="seo" element={<SEOAttributionPage />} />
        <Route path="conversion-labels" element={<Navigate to="../seo?tab=conversion-labels" replace />} />
        <Route path="booking" element={<BookingSettingsPage />} />
        <Route path="balance-reminders" element={<Navigate to="../booking?tab=deposit-settings" replace />} />
        <Route path="pricing" element={<PricingSettingsPage />} />
        <Route path="flat-rates" element={<Navigate to="../pricing?tab=flat-rates" replace />} />
        <Route path="zip-flat-rates" element={<Navigate to="../pricing?tab=zip-flat-rates" replace />} />
        <Route path="deadhead-fees" element={<Navigate to="../pricing?tab=deadhead-fees" replace />} />
        <Route path="airports" element={<AirportSettingsPage />} />
        <Route path="ai-assistant" element={<AIAssistantPage />} />
        <Route path="lead-scoring" element={<LeadScoringPage />} />
        <Route path="integrations" element={<IntegrationsPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="account" element={<AccountSettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="dashboard" replace />} />
    </Routes>
  )
}

// Default export for backwards compatibility
export default BuilderApp
